import { useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import BookConsultationDialog from "@/components/site/BookConsultationDialog";
import JoinUsDialog from "@/components/site/JoinUsDialog";
import FloatingActions from "@/components/site/FloatingActions";
import EditorialHero from "@/components/site/v2/EditorialHero";
import ChallengeCards from "@/components/site/v2/ChallengeCards";
import ServiceExplorer from "@/components/site/v2/ServiceExplorer";
import FrameworkDiagram from "@/components/site/v2/FrameworkDiagram";
import OutcomeStrip from "@/components/site/v2/OutcomeStrip";
import ImpactStrip from "@/components/site/v2/ImpactStrip";
import FaqSection from "@/components/site/v2/FaqSection";
import RelatedLinks from "@/components/site/v2/RelatedLinks";
import ContextualCTA from "@/components/site/v2/ContextualCTA";
import ImageBand from "@/components/site/v2/ImageBand";
import ToolPromo from "@/components/tools/ToolPromo";
import { STRATEGY_PAGE as page } from "@/data/strategyPage";
import { imageForChallenge, serviceImagePool, imageForPath, uniquePicker, ALL_IMAGES } from "@/data/images";
import { track } from "@/lib/analytics";
import { usePageMeta } from "@/lib/usePageMeta";

const CAP_URL = "/capabilities/strategy-commercial-transformation";

export default function StrategyPage() {
  const [bookOpen, setBookOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  usePageMeta({
    title: page.metaTitle,
    description: page.metaDescription,
    canonical: `https://blezzings.co${CAP_URL}`,
    breadcrumbs: [
      { name: "Home", url: "https://blezzings.co/" },
      { name: "Our Services", url: `https://blezzings.co${CAP_URL}` },
      { name: page.title, url: `https://blezzings.co${CAP_URL}` },
    ],
    faqs: page.faqs,
    service: {
      name: page.title,
      description: page.metaDescription,
      url: `https://blezzings.co${CAP_URL}`,
    },
  });

  const openBook = (source) => {
    track("book_consultation_open", { source });
    track("service_cta_click", {
      service: page.title,
      source_page: window.location.pathname,
      cta_location: source,
    });
    setBookOpen(true);
  };

  // No image repeats on the page: hero + band reserved up front, every card
  // image picked uniquely (most relevant first, spare pool as fallback).
  const pick = uniquePicker(
    ["/photos/practice-hero-strategy.jpg", "/photos/practice-band-strategy.jpg"],
    [serviceImagePool("strategy-commercial-transformation", "/photos/practice-hero-strategy.jpg"), ALL_IMAGES]
  );

  const explorerItems = page.offerGroups
    .flatMap((g) => g.offers)
    .map((o) => ({
      name: o.name,
      outcome: o.lead,
      body: o.body,
      covers: o.list,
      link: { to: `${CAP_URL}/${o.slug}`, label: `Explore ${o.name}` },
      image: pick(imageForPath(`${CAP_URL}/${o.slug}`)),
    }));

  const challenges = page.challengeQuestions.map((q) => ({
    title: q,
    to: "#capabilities",
    image: pick(imageForChallenge(q)),
  }));

  const deliverables = page.deliverGroups.flatMap((g) => g.items);

  return (
    <div data-testid="strategy-page" className="relative">
      <Nav onBook={() => openBook("nav")} />

      <EditorialHero
        testid="strategy-hero"
        eyebrow="Our Services"
        title={page.title}
        description={page.heroSubhead}
        tagline={page.heroTagline}
        image="/photos/practice-hero-strategy.jpg"
        imageAlt="Indian strategy team reviewing growth plans on a glass planning wall"
        primaryLabel={page.heroCta}
        onPrimary={() => openBook("strategy-hero")}
        secondaryLabel="Explore the Capabilities"
        secondaryTo="#capabilities"
      />

      <ChallengeCards
        testid="strategy-challenges"
        badge="The Business Challenge"
        headline={page.challengeLead}
        challenges={challenges}
        kicker={page.challengeClosing}
      />

      <div id="capabilities">
        <ServiceExplorer
          testid="strategy-explorer"
          badge="What We Offer"
          headline={page.offersLead}
          intro="Seven connected capabilities. Open any one to see what it covers, what it typically delivers, and where to go deeper."
          items={explorerItems}
          deliverablesLabel="Typical deliverables across engagements"
          deliverables={deliverables}
        />
      </div>

      <FrameworkDiagram
        testid="strategy-framework"
        badge="How We Work"
        headline={page.approachLead}
        steps={page.approach.map((s) => ({ name: s.title, desc: s.body }))}
        kicker={page.approachClosing}
      />

      {/* Quiet cross-link: how engagements unfold */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 -mt-6 pb-2 text-center">
        <Link
          to="/how-we-engage"
          data-testid="strategy-how-we-engage-link"
          className="text-sm text-zinc-500 hover:text-black underline underline-offset-4 decoration-zinc-300 hover:decoration-black transition-colors"
        >
          How engagements work →
        </Link>
      </div>

      <ImageBand
        testid="strategy-band"
        image="/photos/practice-band-strategy.jpg"
        alt={`${page.title} — BLEZZINGS`}
        caption={page.title}
      />

      <ToolPromo pageUrl="/capabilities/strategy-commercial-transformation" testid="practice-tool-promo" />

      <OutcomeStrip
        testid="strategy-outcomes"
        headline={page.outcomesLead}
        items={page.outcomes.slice(0, 6)}
        note={page.outcomesClosing}
      />

      <ImpactStrip
        testid="strategy-impact"
        headline={page.impactLead}
        impact={page.impact}
        note={page.impactNote}
      />

      <RelatedLinks
        testid="strategy-related"
        badge="Related Expertise"
        headline={page.relatedLead}
        links={page.related.map((r) => ({ ...r, image: pick(imageForPath(r.to)) }))}
      />

      <FaqSection testid="strategy-faqs" faqs={page.faqs} />

      <ContextualCTA
        testid="strategy-cta"
        heading={page.ctaHeading}
        body={page.ctaBody}
        label={page.ctaLabel}
        onBook={() => openBook("strategy-cta")}
      />

      <Footer onJoin={() => setJoinOpen(true)} />
      <FloatingActions onBook={() => openBook("floating")} />
      <BookConsultationDialog open={bookOpen} onOpenChange={setBookOpen} />
      <JoinUsDialog open={joinOpen} onOpenChange={setJoinOpen} />
    </div>
  );
}
